import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import clsx from "clsx";
import PropTypes from "prop-types";
import {useI18N, useLocation} from "../../../common/common";
import {BOOKING, GENERAL_TERMS_OF_USE, IMPRINT, MARKET_PLACE, PRIVACY_POLICY, RESORTS_BOOKING_INFORMATION} from "../../../common/routes";
import {useKeycloakFunctions} from "../../../hooks/useKeycloakFunctions";
import CurrencySelect from "../../widget/select/currencySelect/CurrencySelect";
import LanguageSelect from "../../widget/select/languageSelect/LanguageSelect";
import {useBreakpoints} from "../../../hooks/useBreakpoints";

const useStyles = makeStyles(theme => ({
    footerBottom: {
        background: theme.palette.secondary.main,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(3),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        flexDirection: "column",
        flexWrap: "nowrap",
        [theme.breakpoints.up("sm")]: {
            paddingLeft: theme.spacing(5),
        },
        [theme.breakpoints.up("md")]: {
            flexDirection: "row-reverse",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(3.5)
        }
    },
    legalContainer: {
        [theme.breakpoints.up("md")]: {
            flexWrap: "nowrap",
            marginBottom: 0
        }
    },
    divider: {
        marginRight: theme.spacing(2),
        marginLeft: theme.spacing(2)
    },
    rightsContainer: {
        paddingRight: theme.spacing(1)
    },
    onHoverUnderline: {
        "&:hover": {
            textDecorationLine: "underline",
            cursor: "pointer"
        }
    },
    colorWhite: {
        color: theme.palette.text.white
    },
    noWrap: {
        whiteSpace: "nowrap"
    },
    breakWord: {
        wordWrap: "break-word"
    },
    columnReverseContainer: {
        flexDirection: "column",
        [theme.breakpoints.up("md")]: {
            flexDirection: "row-reverse",
            justifyContent: "flex-end"
        }
    },
    selectionContainer: {
        flexDirection: "column",
        [theme.breakpoints.up("md")]: {
            flexDirection: "row",
        }
    },
    flagIcon: {
        marginRight: theme.spacing(2.5),
        marginLeft: theme.spacing(-1),
        height: theme.spacing(3),
        width: theme.spacing(3),
    },
    currencyList: {
        [theme.breakpoints.down("sm")]: {
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(1),
        }
    },
    languageList: {
        [theme.breakpoints.down("sm")]: {
            marginBottom: theme.spacing(5)
        }
    },
    button: {
        "&:hover": {
            textDecoration: "none"
        },
    },
    pr3: {
        paddingRight: theme.spacing(3)
    }
}));

const FooterBottom = ({companyDetails}) => {

    const classes = useStyles();
    const {navigate, isRouteInLocation} = useLocation();
    const {isAdmin} = useKeycloakFunctions();
    const {i18n} = useI18N();
    const {isExtraSmallScreen, isSmallScreen} = useBreakpoints();
    const copyRightDate = new Date().getFullYear();

    return (
        <Grid
            container
            className={classes.footerBottom}
        >
            <Grid
                item
            >
                <Grid
                    container
                    wrap="nowrap"
                    className={classes.selectionContainer}
                >
                    {!isRouteInLocation(MARKET_PLACE) && !isRouteInLocation(BOOKING) && !isRouteInLocation(RESORTS_BOOKING_INFORMATION) && (
                        <>
                            <Grid
                                item
                                className={classes.currencyList}
                            >
                                <>
                                    {(isSmallScreen() || isExtraSmallScreen()) ? (
                                        <CurrencySelect
                                            type={"listOverlay"}
                                            showSelectedIndicator
                                            hideDividers
                                            location={"footer"}
                                        />

                                    ) : (
                                        <CurrencySelect
                                            type={"popover"}
                                            noColorHover={classes.button}
                                            textColor={classes.colorWhite}
                                            isFooter
                                            location={"footer"}
                                        />
                                    )}
                                </>
                            </Grid>
                        </>
                    )}
                    {!isRouteInLocation(BOOKING) && (
                        <>
                            <Grid
                                item
                                className={classes.languageList}
                            >

                                <>
                                    {(isSmallScreen() || isExtraSmallScreen()) ? (
                                        <LanguageSelect
                                            type={"listOverlay"}
                                            showSelectedIndicator
                                            hideDividers
                                            iconClass={classes.flagIcon}
                                            location={"footer"}
                                        />
                                    ) : (
                                        <LanguageSelect
                                            type={"popover"}
                                            location={"footer"}
                                        />
                                    )}
                                </>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Grid>
            <Grid
                item
            >
                <Grid
                    container
                    spacing={3}
                    className={classes.columnReverseContainer}
                >
                    <Grid
                        item
                    >
                        <Grid
                            container
                            direction={"row"}
                            className={classes.legalContainer}
                        >
                            <Grid
                                item
                            >
                                <Typography
                                    className={classes.colorWhite}
                                >
                                    <span
                                        className={clsx(classes.onHoverUnderline)}
                                        onClick={() => navigate(IMPRINT + PRIVACY_POLICY)}
                                    >
                                        {i18n("layout.footer.privacy")}
                                    </span>
                                    <span
                                        className={clsx(classes.divider, classes.breakWord)}
                                    >
                                        {"|"}
                                    </span>

                                </Typography>
                            </Grid>
                            <Grid
                                item
                            >
                                <Typography
                                    className={classes.colorWhite}
                                >
                                    <span
                                        className={clsx(classes.onHoverUnderline)}
                                        onClick={() => navigate(IMPRINT + GENERAL_TERMS_OF_USE)}
                                    >
                                        {i18n("layout.footer.terms")}
                                    </span>

                                    <span
                                        className={clsx(classes.divider, classes.breakWord)}
                                    >
                                        {"|"}
                                    </span>
                                </Typography>
                            </Grid>
                            <Grid
                                item
                            >
                                <Typography
                                    className={clsx(classes.colorWhite)}
                                >
                                    <span
                                        className={clsx(classes.onHoverUnderline)}
                                        onClick={() => navigate(IMPRINT)}
                                    >
                                        {i18n("layout.footer.imprint")}
                                    </span>

                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid
                        item
                        className={classes.pr3}
                    >
                        <Grid
                            container
                            direction={"row"}
                        >
                            <Grid
                                item
                            >
                                <Typography
                                    variant="h6"
                                    component="div"
                                    className={clsx(classes.colorWhite, classes.noWrap, classes.rightsContainer)}
                                >
                                    {"© " + copyRightDate + " " + companyDetails?.contactDetails?.companyName + "."}
                                </Typography>

                            </Grid>

                            <Grid
                                item
                            >
                                <Typography
                                    className={clsx(classes.colorWhite, classes.noWrap)}
                                >
                                    {isAdmin() ? (
                                        i18n("layout.footer.admin") + ", " + i18n("common.label.version") + " " + process?.env?.APP_VERSION
                                    ) : (
                                        i18n("layout.footer.rights_reserved")
                                    )}
                                </Typography>

                            </Grid>
                        </Grid>
                    </Grid>


                </Grid>
            </Grid>

        </Grid>
    );
};

FooterBottom.propTypes = {
    companyDetails: PropTypes.object,
};

export default FooterBottom;
